import { observer } from 'mobx-react';
import IconButton, {
  ICON_BUTTON_ICONS,
  ICON_BUTTON_TYPES
} from '../../../../components/buttons/IconButton';
import { UI_OPTIONS } from '../../../../utils/constants/uiOptions';
import { UI_OPTION_KEYS } from '../../../../utils/constants/uiOptionKeys';
import {
  isProfilerListVisible,
  PROFILER_SEARCH_RESULTS_COLUMNS,
  PROFILER_VIEWS
} from '../constants/constants';
import GeneralLoader from '../../../../components/loaders/GeneralLoader';
import GeneralError from '../../../../components/errors/GeneralError';
import NoResults from './NoResults';
import ProfilerSearchResultsLoaderNew from './ProfilerSearchResultsLoaderNew';
import ProfilerBarChart from '../../../../components/profiler/ProfilerBarChart/ProfilerBarChart';
import ProfilerHeatmapSection from '../../../../components/profiler/HeatmapSection/ProfilerHeatmapSection';
import ProfilerColumnHeaderRow from '../../../../components/profiler/ProfilerColumnHeaderRow/ProfilerColumnHeaderRow';
import CompanyResult from './CompanyResult';
import FundResult from './FundResult';
import PortfolioResult from './PortfolioResult';
import InvestProfileResult from './InvestProfileResult';
import Pagination from '../../../../components/navigation/Pagination';
import { hasAnyActiveFilter } from '../constants/exploreFilterEngine';
import { PAGE_SIZE as pageSize } from '../getResultsHelpers';
import { paths } from '../../../../utils/constants/routes';

/**
 * The results-body region of SearchResults (loaders, error, empty state, the
 * Profiler bar-chart / heatmap views, the paginated result list, the demo-limit
 * banner, the excluded-results panel and the pager).
 *
 * Extracted verbatim from SearchResults/index.jsx (TD-2377 — SonarQube
 * cognitive-complexity cleanup, Phase 3 Part B). This block held the bulk of the
 * component body's direct JSX conditional gates; moving it into its own
 * `observer` component lifts that complexity out of the parent arrow (each
 * function is scored independently). Behaviour-preserving: the derived flags and
 * `navigateToCompanyDetails` are computed in the parent and passed through, so no
 * logic is duplicated or re-derived here.
 */
const SearchResultsBody = observer(
  ({
    state,
    isLoading,
    hasResults,
    hasError,
    showNoResults,
    showResults,
    showPaging,
    currentSearchType,
    excludedResults,
    profilerColumns,
    profilerColumnHeaderJSX,
    profilerSearchLoader,
    navigateToCompanyDetails,
    navigate,
    location,
    authStore,
    profilerAccessStore
  }) => {
    /**
     * One result row, by search type.
     *
     * TD-2720 — this four-way choice was written as a nested ternary chain, twice:
     * once for the paginated list and once for the excluded-results panel, differing
     * only in two props. Both copies now come through here, so the row markup for a
     * search type lives in exactly one place.
     *
     * `inExcludedPanel` keeps the one real difference: the excluded panel does not
     * pass `searchId` / `setIsAnyOutreachButtonHovered` to the non-Profiler rows, so
     * their outreach hover wiring stays inert there, exactly as before.
     */
    const renderResultRow = (entry, { inExcludedPanel = false } = {}) => {
      const searchType = state.searchType[0];

      if (searchType === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler) {
        return (
          <CompanyResult
            key={entry.id}
            company={entry}
            displayDetails={navigateToCompanyDetails}
            excludeResult={state.onExcludeResult}
            isInExcludeAction={!!state.excludingResults[entry.id]}
            searchId={state.searchId}
            searchKey={state.profilerNameSearchSearchKey}
            profilerColumns={profilerColumns}
            isProfilerExtendedFinancialsToggled={state.isProfilerExtendedFinancialsToggled}
            onPillClick={state.togglePillFilter}
            columnFilters={state.columnFilters}
            ownershipDisplayBasis={state.ownershipDisplayBasis}
            ownershipSearchedBasis={state.ownershipSearchedBasis}
            ownershipSearchedStructures={state.currentSearch?.ownershipStructures}
          />
        );
      }

      const entryProps = {
        entry,
        displayMessagePopup: state.displayMessagePopup,
        displayDetailsPopup: state.displayDetailsPopup,
        projectId: state.projectId,
        excludeResult: state.onExcludeResult,
        isInExcludeAction: !!state.excludingResults[entry.id],
        ...(inExcludedPanel
          ? {}
          : {
              searchId: state.searchId,
              setIsAnyOutreachButtonHovered: state.setIsAnyOutreachButtonHovered
            })
      };

      if (searchType === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Funds) {
        return <FundResult key={entry.id} {...entryProps} />;
      }

      if (searchType === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE]['Portfolio Co.']) {
        return <PortfolioResult key={entry.id} {...entryProps} />;
      }

      return <InvestProfileResult key={entry.id} {...entryProps} />;
    };

    return (
      <>
        {isLoading &&
          (currentSearchType === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler ? (
            state.searchCategory ===
              UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Characteristics'] &&
            !state.loadingSearchId ? (
              <ProfilerSearchResultsLoaderNew
                wrapClassname="in-search-results-page"
                loaderClassname="in-search-results-page"
                componentProps={profilerSearchLoader.componentProps}
              />
            ) : (
              <GeneralLoader />
            )
          ) : (
            <GeneralLoader />
          ))}

        {hasError && (
          <GeneralError
            errorMessage={state.resultsError}
            actionMessage="You may want to:"
            {...(state.failedReqParams
              ? {
                  actionButtontext: 'Try again',
                  onActionButtonClick: () => state.getResults(...state.failedReqParams)
                }
              : { withHomePageButton: true })}
            withLogoutButton={currentSearchType === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler}
          />
        )}

        {showNoResults && <NoResults />}

        {showResults &&
          state.searchType[0] === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler &&
          state.profilerView === PROFILER_VIEWS.CHART && (
            <ProfilerBarChart
              companies={state.sortedResults}
              onCompanyClick={navigateToCompanyDetails}
              chartId={state.searchId}
              project={{ ownerId: authStore.currentUserId, searchId: state.searchId }}
              sort={state.selectedSort}
              getIsExcludingResult={state.getIsExcludingResult}
              excludeResult={state.onExcludeResult}
            />
          )}

        {showResults &&
          state.searchType[0] === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler &&
          state.profilerView === PROFILER_VIEWS.HEATMAP && (
            <ProfilerHeatmapSection
              controls={state}
              // TD-2200 — the tiles follow the active filters, so a chip removal
              // redraws both panes together. `sortedResults` stays unfiltered: the
              // Chart view, the download-limit gates and the "X out of Y" count
              // denominator all read it.
              results={state.filteredAndSortedResults}
              searchId={state.searchId}
              searchCategory={state.searchCategory}
              groupProperty={state.profilerHeatmapGroup}
              layout={state.layout}
              isCollapsed={state.isProfilerHeatmapCollapsed}
              onToggleCollapse={state.toggleProfilerHeatmapCollapsed}
              isLoading={isLoading}
              hasResults={hasResults}
              showCagr4Y={
                state.profilerHeatmapColorCoding ===
                  UI_OPTIONS[UI_OPTION_KEYS.PROFILER_HEATMAP_COLOR_CODING]['CAGR 4Y (%)'] ||
                (profilerColumns.some(
                  (column) =>
                    column.id === PROFILER_SEARCH_RESULTS_COLUMNS.CAGR4Y && column.selected
                ) &&
                  state.profilerHeatmapColorCoding !==
                    UI_OPTIONS[UI_OPTION_KEYS.PROFILER_HEATMAP_COLOR_CODING]['CAGR 3Y (%)'])
              }
              showEbitMargin={!profilerAccessStore?.isProfilerLiteOnly}
              displayDetails={navigateToCompanyDetails}
              getIsExcludingResult={state.getIsExcludingResult}
              excludeResult={state.onExcludeResult}
              setToggleProfilerHeatmapGroupingCallback={
                state.setToggleProfilerHeatmapGroupingCallback
              }
            />
          )}

        {/* TD-2720 — the list renders in List view and under the heatmap in Heatmap
           view; only Chart view still replaces it. */}
        {showResults &&
          (state.searchType[0] !== UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler ||
            (state.searchType[0] === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler &&
              isProfilerListVisible(state.profilerView))) && (
            <>
              {/* TD-2720 — the Profiler column-header row now sits directly above
                 the rows instead of inside the sticky `.results-header`, so the
                 stacked heatmap view can put the heatmap above it. Profiler only:
                 Funds / Portfolios / Corporates keep their `.funds-search-cols`
                 header in `renderStandardResultsHeader`. */}
              {state.searchType[0] === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler && (
                <ProfilerColumnHeaderRow
                  profilerColumns={profilerColumns}
                  columnHeaderJSX={profilerColumnHeaderJSX}
                  isColumnVisible={(column) => column.selected}
                />
              )}
              <div className="results-wrap">
                {/* TD-2200 V1 — List view reads from `filteredAndSortedResults`.
                 For non-Profiler search types the activeSpotlights / columnFilters
                 state stays empty, so this resolves to the same set as
                 `sortedResults` (no behavior change for Funds/Portfolios/etc.). */}
                {/* TD-2200 V1 — explicit empty state when active Spotlights /
                 column filters narrow the list to nothing, so the results
                 area isn't just blank. Gated on `hasAnyActiveFilter` so an
                 all-excluded set (no filters) doesn't get mislabelled. */}
                {state.filteredAndSortedResults.length === 0 &&
                  hasAnyActiveFilter(state.activeSpotlights, state.columnFilters) && (
                    <div className="no-filter-results">No companies match the active filters.</div>
                  )}
                {state.filteredAndSortedResults
                  .slice((state.page - 1) * pageSize, state.page * pageSize)
                  .map((entry) => renderResultRow(entry))}
              </div>
            </>
          )}

        {state.searchType[0] === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler &&
          showResults &&
          profilerAccessStore.isProfilerDemo &&
          state.fullResultsCount > pageSize &&
          isProfilerListVisible(state.profilerView) && (
            <div className="profiler-demo-limited-results-row">
              <div className="icon-wrap">
                <img src={ICON_BUTTON_ICONS.EXCLAMATAION_CIRCLE} alt="" />
              </div>
              <div className="content">
                <div className="title">Limited results shown in demo mode</div>
                <div className="text">
                  You&apos;re viewing {pageSize} of {state.fullResultsCount} results. Contact{' '}
                  <a href="mailto:support@transact.digital">support@transact.digital</a> for a
                  subscription to see the full list.
                </div>
              </div>
            </div>
          )}

        {showResults &&
          !!excludedResults.length &&
          (state.searchType[0] !== UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler ||
            (state.searchType[0] === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler &&
              isProfilerListVisible(state.profilerView))) && (
            <div className="excluded-results-wrap">
              <div className="excluded-results-box">
                <div
                  className={`excluded-results-header${
                    state.isExcludedExpanded ? ' expanded' : ''
                  }`}
                >
                  <div className="excluded-results-header-text">
                    Excluded results ({excludedResults.length})
                  </div>
                  <IconButton
                    type={ICON_BUTTON_TYPES.DEFAULT}
                    icon={
                      state.isExcludedExpanded
                        ? ICON_BUTTON_ICONS.CHEVRON_UP
                        : ICON_BUTTON_ICONS.CHEVRON_DOWN
                    }
                    innerText={state.isExcludedExpanded ? 'Collapse' : 'Expand  '}
                    iconOnRight
                    withBorder={false}
                    onClick={state.toggleExcludedExpanded}
                    hoverType={ICON_BUTTON_TYPES.BLUE}
                  />
                </div>
                {state.isExcludedExpanded && (
                  <div>
                    {excludedResults.map((entry) =>
                      renderResultRow(entry, { inExcludedPanel: true })
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

        <Pagination
          show={showPaging}
          onPageChange={(selected) => {
            const urlParams = new URLSearchParams(location.search);
            const searchCategory = decodeURIComponent(urlParams.get('searchCategory'));
            const fieldsParams = Object.entries(state.currentSearch)
              .map(([k, v]) => `${[k]}=${encodeURIComponent(JSON.stringify(v))}`)
              .join('&');
            navigate(
              paths.SEARCH_RESULTS +
                `?searchCategory=${encodeURIComponent(searchCategory)}&page=${selected + 1}&${
                  state.searchId ? `searchId=${state.searchId}&` : ''
                }${fieldsParams}`
            );
            setTimeout(() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 100);
          }}
          page={state.page - 1}
          pages={state.pages}
          desktopInitialPageRange={3}
          desktopInitialPageMargin={3}
          desktopRangeStep={300}
        />
      </>
    );
  }
);

export default SearchResultsBody;
